/**
 * Build-time configuration (dev.md §5: `VITE_`-prefixed, and only wiring).
 *
 * Three things here are decisions rather than plumbing.
 *
 * **The API base is empty in development.** `admin-api` mounts no CORS
 * middleware — it is a same-origin service behind restricted ingress — so a
 * browser on `:5174` talking to `:8084` would be refused by the preflight.
 * Vite's dev server proxies `/v1` and `/healthz` instead, which makes the
 * development origin the same origin, which is the shape production has.
 *
 * **The variable is `VITE_ADMIN_API_URL`, not `VITE_CORE_API_URL`.** dev.md §5
 * lists one row for `app` / `admin` naming `VITE_CORE_API_URL`, which is a
 * discrepancy rather than an instruction: the console never speaks to
 * `core-api` — it holds no tenant credential and `core-api` would refuse an
 * operator token, which carries no `tenant_id` (see `admin-api`'s
 * `token-verifier.ts`). Pointing this build at `core-api` would produce a
 * console where every screen 401s. The doc's row needs splitting; it is noted
 * in the hand-off rather than silently followed.
 *
 * **`VITE_AUTH_FAKE` is a development affordance and says so.** It mints the
 * unsigned token shape `admin-api`'s `FakeVerifier` accepts, which that class
 * refuses to honour when `NODE_ENV=production` — a refusal that matters more
 * here than anywhere else, because an unsigned token accepted on this surface
 * is not one tenant's book, it is every tenant's. The console carries the same
 * asymmetry: the flag is read once, here, and the sign-in screen states which
 * mode it is in rather than looking identical to the real one.
 */

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
}

export interface ConsoleConfig {
  /** `''` means same-origin (the dev proxy, or Hosting rewrites). */
  apiBaseUrl: string;
  auth: { mode: 'firebase'; firebase: FirebaseConfig } | { mode: 'fake' };
  /** Rows per page on the fleet, the audit log and the delivery log. */
  pageSize: number;
  /**
   * Where a tenant's own staff sign in — the catalog step of the onboarding
   * wizard links here, because seeding a catalog is something this console
   * deliberately cannot do (spec §6.3). A pattern, `{tenant}` substituted.
   */
  staffAppUrlPattern: string;
}

interface RawEnv {
  VITE_ADMIN_API_URL?: string | undefined;
  VITE_FIREBASE_API_KEY?: string | undefined;
  VITE_FIREBASE_AUTH_DOMAIN?: string | undefined;
  VITE_FIREBASE_PROJECT_ID?: string | undefined;
  VITE_FIREBASE_APP_ID?: string | undefined;
  VITE_AUTH_FAKE?: string | undefined;
  VITE_STAFF_APP_URL?: string | undefined;
  DEV?: boolean | undefined;
}

export const DEFAULT_STAFF_APP_URL = 'https://app-test.yourstore.jswaves.com';

export function readConfig(env: RawEnv): ConsoleConfig {
  const fake = env.VITE_AUTH_FAKE === '1' || env.VITE_AUTH_FAKE === 'true';

  return {
    apiBaseUrl: env.DEV === true ? '' : trimSlash(env.VITE_ADMIN_API_URL ?? ''),
    auth: fake
      ? { mode: 'fake' }
      : {
          mode: 'firebase',
          firebase: {
            apiKey: env.VITE_FIREBASE_API_KEY ?? '',
            authDomain: env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
            projectId: env.VITE_FIREBASE_PROJECT_ID ?? '',
            appId: env.VITE_FIREBASE_APP_ID ?? '',
          },
        },
    pageSize: 50,
    staffAppUrlPattern: trimSlash(env.VITE_STAFF_APP_URL ?? DEFAULT_STAFF_APP_URL),
  };
}

function trimSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

export const config: ConsoleConfig = readConfig(import.meta.env as unknown as RawEnv);
